//IMPORTAÇÃO DAS BIBLIOTECAS---------------------------------------------------
import { useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer } from 'react-toastify';



//IMPORTAÇÃO DOS COMPONENTES---------------------------------------------------
import styles from './myApp.module.css';
import { GoX } from "react-icons/go";
import { BiLogoPlayStore } from "react-icons/bi";
import { FaRegShareFromSquare } from "react-icons/fa6";
import { FiLink2 } from "react-icons/fi";
import { useAppContext } from "../../../../AppContext.js";



// COMPONENTE------------------------------------------------------------------
const MyApp = ({ open, onClose }) => {

  // hooks
  const { t } = useTranslation(); // Hook de tradução
  const { isApp } = useAppContext(); // Hook de contexto



  // Gerencia efeitos colaterais do modal (scroll lock, fechar com ESC).
  useEffect(() => {
    if (!open) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);

    // Função de limpeza
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEsc);
    };
  }, [open, onClose]);



  // Abre a página de avaliação na Play Store (tenta market:// no Android, faz fallback para web).
  // Configure o package name com a variável de ambiente REACT_APP_PLAY_STORE_ID ou REACT_APP_ANDROID_PACKAGE
  const PLAY_STORE_ID = "com.toskyapps.tsnotes";
  const openPlayStore = () => {
    const id = PLAY_STORE_ID;
    const webUrl = `https://play.google.com/store/apps/details?id=${id}`;
    const marketUrl = `market://details?id=${id}`;
    if (!id || id.includes('COLOQUE')) {
      // sem package configurado: abre a página principal da Play Store
      window.open('https://play.google.com/store', '_blank', 'noopener');
      return;
    }
    try {
      if (/Android/i.test(navigator.userAgent)) {
        // tenta abrir o app do Play Store no Android; se não ocorrer, faz fallback para a URL web
        window.location.href = marketUrl;
        setTimeout(() => window.open(webUrl, '_blank', 'noopener'), 800);
      } else {
        window.open(webUrl, '_blank', 'noopener');
      }
    } catch (e) {
      window.open(webUrl, '_blank', 'noopener');
    }
  };



  // Abre a caixa de compartilhamento; tenta Web Share API, depois ponte nativa (WebView), depois clipboard, por fim fallback para Play Store
  const shareApp = async () => {
    const id = PLAY_STORE_ID;
    const url = isApp ? `https://play.google.com/store/apps/details?id=${id}` : `https://tsnotes.toskyapps.com`;
    if (!id || id.includes('COLOQUE')) {
      openPlayStore();
      return;
    }

    // 1) Web Share API (Chrome/TWA)
    if (navigator.share) {
      try {
        await navigator.share({ url });
        return;
      } catch (err) {
        // usuário cancelou ou erro — continuar para fallback
        console.debug('navigator.share error:', err);
      }
    }

    // 2) Android WebView bridge (host app should add addJavascriptInterface)
    if (window.Android && typeof window.Android.share === 'function') {
      try {
        window.Android.share(`${url}`);
        return;
      } catch (err) {
        console.debug('Android.share error:', err);
      }
    }

    // 3) copiar para clipboard + feedback
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        // acessível e simples: alerta (pode ser substituído por um toast)
        toast.success(t('Link copied to clipboard'));
        return;
      } catch (err) {
        console.debug('clipboard error:', err);
      }
    }

    // 4) fallback final: abrir Play Store web
    window.open(url, '_blank', 'noopener');
  };



  // Copia um link específico (usado pelo ícone). Prevém a navegação do <a> quando clicado.
  const handleCopyLink = async (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    if (!url) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      // feedback simples (consistente com shareApp)
      toast.success(t('Link copied to clipboard!'));
    } catch (err) {
      console.debug('copy error:', err);
      // fallback: abrir o link
      window.open(url, '_blank', 'noopener');
    }
  };



  // accessibility: focus management (moved above early return)
  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);
  const titleId = 'myapp-title';
  const descId = 'myapp-desc';
  useEffect(() => {
    if (open) {
      // foca o botão fechar para acessibilidade
      closeBtnRef.current?.focus?.();
    }
  }, [open]);

  //modal fechado ao iniciar
  if (!open) return null;

  // fecha quando clica no overlay (fora do modal)
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };



  // html do componente
  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <aside
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        ref={modalRef}
      >

        {/* header */}
        <div className={styles.headerMessages}>
          <div className={styles.headerContent}>
            <div className={styles.appBadge} aria-hidden>
              <span>tS</span>
            </div>
            <div className={styles.titles}>
              <h2 id={titleId}>{t('My App')}</h2>
              <p className={styles.subtitle}>{t('Quick notes, everywhere — sync across devices')}</p>
            </div>
          </div>

          <button
            className={styles.iconButton}
            onClick={onClose}
            aria-label={t('Close')}
            ref={closeBtnRef}
            title={t('Close')}
          >
            <GoX />
          </button>
        </div>

        <div className={styles.body} id={descId}>

          {/* features */}
          <ul className={styles.features}>
            <li className={styles.featureItem}><span className={styles.featureIcon}>✓</span>{t('Automatic cloud synchronization')}</li>
            <li className={styles.featureItem}><span className={styles.featureIcon}>🔒</span>{t('Private and secure notes')}</li>
            <li className={styles.featureItem}><span className={styles.featureIcon}>⚡</span>{t('Fast, lightweight and simple')}</li>
          </ul>

          {/* botões de avaliação e compartilhamento */}
          <div className={styles.ctaRow}>

            {/* rate this app */}
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={openPlayStore}
              aria-label={t('Rate this app')}
              title={t('Rate this app')}
            >
              <BiLogoPlayStore className={styles.playIcon} />
              <span>{t('Rate this app')}</span>
            </button>

            {/* share this app */}
            <button
              type="button"
              className={styles.shareBtn}
              onClick={shareApp}
              aria-label={t('Share this app')}
              title={t('Share this app')}
            >
              <FaRegShareFromSquare className={styles.shareIcon} />
              <span>{t('Share')}</span>
            </button>
          </div>

          {/* links */}
          <div className={styles.linkGrid}>
            {/* web app ou app android */}
            <label className={styles.linkCard}>
              <div>
                <strong>{isApp ? t('Web app') : t('Android app')}</strong>
                <span className={styles.linkUrl}>{isApp ? 'www.tsnotes.toskyapps.com' : 'https://play.google.com/store/apps/details?id=com.toskyapps.tsnotes'}</span>
              </div>

              <button
                type="button"
                className={`${styles.linkIcon} ${styles.iconButton}`}
                onClick={(e) => handleCopyLink(e, isApp ? 'https://www.tsnotes.toskyapps.com' : 'https://play.google.com/store/apps/details?id=com.toskyapps.tsnotes')}
                aria-label={t('Copy link')}
                title={t('Copy link')}
              >
                <FiLink2 aria-hidden />
              </button>
            </label>

            {/* conta sky apps */}
            <a className={styles.linkCard} href="https://www.toskyapps.com" target="_blank" rel="noopener noreferrer">
              <div>
                <strong>{t('Manage your account Sky Apps')}</strong>
                <span className={styles.linkUrl}>www.toskyapps.com</span>
              </div>

              <button
                type="button"
                className={`${styles.linkIcon} ${styles.iconButton}`}
                onClick={(e) => handleCopyLink(e, 'https://www.toskyapps.com')}
                aria-label={t('Copy link')}
                title={t('Copy link')}
              >
                <FiLink2 aria-hidden />
              </button>
            </a>

            {/* tutorials */}
            {/* <a className={styles.linkCard} href="https://www.youtube.com/playlist?list=PL5bYH1vI6kXK0b1yXW0ZlZJ8dUu0j_L8N" target="_blank" rel="noopener noreferrer">
              <div>
                <strong>{t('Tutorials')}</strong>
                <div className={styles.linkMeta}>{t('Vídeos no YouTube')}</div>
              </div>
              <span className={styles.linkUrl}>YouTube</span>
            </a> */}
          </div>

          {/* footer */}
          <footer className={styles.footer}>
            <div className={styles.footerLeft}>{t('Thank you for using tS Notes — feedback is welcome!')}</div>
            <div className={styles.footerRight}>
              <a href="https://play.google.com/store/apps/details?id=com.toskyapps.tsnotes" target="_blank" rel="noopener noreferrer">{t('Open in Play Store')}</a>
            </div>
          </footer>
        </div>
      </aside>

      {/* TOAST CONTAINER - ALERTAS RÁPIDOS AO USUÁRIO */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={document.body.classList.contains("theme-dark") ? "dark" : "light"}
      />
    </div>
  );
};

export default MyApp;